import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import useSiteText from "../hooks/useSiteText";
import { Menu, X, Phone } from "lucide-react";
import Picture from "./Picture";
import logoWebp from "../assets/tamari-logo.webp";
import logoPng from "../assets/tamari-logo.png";

const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const text = useSiteText();
  const phone = text("contact.phone", "+355 69 624 2511");
  const phoneHref = `tel:${phone.replace(/\s/g, "")}`;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: "/", label: t.nav.home },
    { to: "/services", label: t.nav.services },
    { to: "/salami", label: t.nav.salami },
    { to: "/sustainability", label: t.nav.sustainability },
    { to: "/about", label: t.nav.about },
    { to: "/gallery", label: t.nav.gallery },
    { to: "/faq", label: t.nav.faq },
    { to: "/contact", label: t.nav.contact }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="header-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-white border-b border-zinc-200" : "bg-white/95"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="header-logo-link">
            <Picture
              webpSrc={logoWebp}
              fallbackSrc={logoPng}
              alt="Tamari"
              loading="eager"
              className="h-12 md:h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`text-sm font-medium tracking-wide transition-colors ${
                  isActive(link.to) ? "text-[#0A0A0A]" : "text-zinc-500 hover:text-[#0A0A0A]"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="hidden xl:flex items-center gap-4">
            <a 
              href={phoneHref} 
              className="flex items-center gap-2 text-sm font-medium text-[#0A0A0A] hover:text-zinc-600 transition-colors"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              {phone}
            </a>
            <div className="flex" data-testid="lang-switch-button">
              <button
                onClick={() => setLang("sq")}
                className={`px-3 py-1 text-sm font-medium border transition-colors ${
                  lang === "sq" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-zinc-300 hover:border-[#0A0A0A]"
                }`}
              >
                AL
              </button>
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1 text-sm font-medium border transition-colors ${
                  lang === "en" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-zinc-300 hover:border-[#0A0A0A]"
                }`}
              >
                EN
              </button>
            </div>
            <Link to="/admin" className="text-sm text-zinc-400 hover:text-zinc-600">
              {t.nav.admin}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden p-2"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-white z-50 xl:hidden overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between h-16">
              <Picture webpSrc={logoWebp} fallbackSrc={logoPng} alt="Tamari" loading="eager" className="h-12 w-auto" />
              <button onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
                <X className="w-6 h-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-4 py-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-xl font-medium py-2 ${isActive(link.to) ? "text-[#0A0A0A]" : "text-zinc-500"}`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => setLang("sq")}
                  className={`px-4 py-2 text-sm font-medium border ${
                    lang === "sq" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A]"
                  }`}
                >
                  AL
                </button>
                <button
                  onClick={() => setLang("en")}
                  className={`px-4 py-2 text-sm font-medium border ${
                    lang === "en" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A]"
                  }`}
                >
                  EN
                </button>
              </div>
              <Link to="/admin" className="text-zinc-400 mt-4">{t.nav.admin}</Link>
              <a 
                href={phoneHref} 
                className="flex items-center gap-2 text-[#0A0A0A] font-medium mt-4"
              >
                <Phone className="w-4 h-4" />
                {phone}
              </a>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
